export interface BodyFatCategory {
    cat: string;
    min: number;
    max: number;
    color?: string;
    /** Paired foreground for text rendered ON the band. */
    textColor?: string;
}


/**
 * The bands for a gender and age. Empty when the age is unknown — a client
 * created with neither an age nor a birth date — so no band word is shown,
 * rather than one from a table that may not be theirs.
 */
export const getBodyFatTable = (
    gender: 'male' | 'female',
    age: number | null | undefined,
): BodyFatCategory[] => {
    if (typeof age !== 'number' || !Number.isFinite(age)) return [];
    const categories = [
        {
            ageRange: [18, 39],
            gender: 'male',
            categories: [
                { cat: "Underfat", min: 0, max: 9 },
                { cat: "Healthy", min: 9, max: 20 },
                { cat: "Overfat", min: 20, max: 25 },
                { cat: "Obese", min: 25, max: 40 },
            ]
        },
        {
            ageRange: [40, 59],
            gender: 'male',
            categories: [
                { cat: "Underfat", min: 3, max: 11 },
                { cat: "Healthy", min: 11, max: 21 },
                { cat: "Overfat", min: 21, max: 27 },
                { cat: "Obese", min: 27, max: 42 },
            ]
        },
        {
            ageRange: [60, 79],
            gender: 'male',
            categories: [
                { cat: "Underfat", min: 4, max: 13 },
                { cat: "Healthy", min: 13, max: 22 },
                { cat: "Overfat", min: 22, max: 30 },
                { cat: "Obese", min: 30, max: 45 },
            ]
        },
        {
            ageRange: [18, 39],
            gender: 'female',
            categories: [
                { cat: "Underfat", min: 8, max: 21 },
                { cat: "Healthy", min: 21, max: 33 },
                { cat: "Overfat", min: 33, max: 39 },
                { cat: "Obese", min: 39, max: 45 },
            ]
        },
        {
            ageRange: [40, 59],
            gender: 'female',
            categories: [
                { cat: "Underfat", min: 8, max: 23 },
                { cat: "Healthy", min: 23, max: 35 },
                { cat: "Overfat", min: 35, max: 40 },
                { cat: "Obese", min: 40, max: 55 },
            ]
        },
        {
            ageRange: [60, 79],
            gender: 'female',
            categories: [
                { cat: "Underfat", min: 8, max: 24 },
                { cat: "Healthy", min: 24, max: 38 },
                { cat: "Overfat", min: 38, max: 44 },
                { cat: "Obese", min: 44, max: 55 },
            ]

        }
    ]
    
    // The tables stop at 18 and 79; clients outside that use the nearest band.
    const tableAge = Math.min(Math.max(age, 18), 79);
    const category:BodyFatCategory[] = categories.find((cat) => {
        return cat.gender === gender && cat.ageRange[0] <= tableAge && cat.ageRange[1] >= tableAge} )?.categories as BodyFatCategory[];
    if (!category) {
        return [];
    }

    // NOTE: nothing renders these colours any more.
    //
    // They were validated against the palette checker and were a real
    // improvement on the pastels they replaced (normal-vision separation
    // 7.0 -> 16.8, tritan 1.9 -> 18.2). But no four-band palette passes every
    // check -- green-amber-red adjacent IS the protanopia confusion axis, and
    // the best achievable separation there is dE 6.1, which the guidance
    // permits only alongside secondary encoding. The conclusion of that work
    // was that the band *labels* were doing the load-bearing job.
    //
    // The concept drops the traffic light outright, so the strip went with it
    // and `cat` is now the whole encoding -- rendered as a word beside the
    // figure in measurement-card.tsx. Kept here rather than deleted because
    // the min/max boundaries these sit on are still the live lookup, and
    // because re-deriving a validated palette is expensive if a chart ever
    // wants one.
    //
    // textColor was the WCAG-checked pairing for a value drawn on the band:
    // ink clears 4.91:1 on the first three, but only 2.47:1 on obese, which
    // therefore took white at 7.58:1.
    const bands = [
        { color: '#7ec0e8', textColor: '#0b1220' }, // underfat
        { color: '#4f9153', textColor: '#0b1220' }, // healthy
        { color: '#c98a1e', textColor: '#0b1220' }, // overfat
        { color: '#8c3a34', textColor: '#ffffff' }, // obese
    ];

    bands.forEach((b, i) => {
        if (category[i]) {
            category[i].color = b.color;
            category[i].textColor = b.textColor;
        }
    });
    return category;

}